import type { ConsumedTaskItem, TaskItem } from "@common/types/Inventory";

import { Flex } from "components/core/Flex";
import { Grid, GridItem } from "components/core/Grid";
import { NumberInput } from "components/core/NumberInput";
import { TruncatedText } from "components/core/TruncatedText";
import resolveInventoryUnit from "helpers/resolveInventoryUnit";
import { useIsMobile } from "hooks";

type Props = {
  item: TaskItem;
  onChange: ({ id, consumedQuantity }: ConsumedTaskItem) => void;
};

const FALLBACK_GARDEN_TITLE = "No garden assigned";

// The row splits 60/40, which is five columns spanned 3 and 2 — the same
// proportion the old `flex: 0.6` / `flex: 0.4` pair produced, but on a track
// the browser sizes rather than a growth factor. Grid tracks are
// `minmax(0, 1fr)`, so the ellipsis works without a `min-width: 0` on each side.
const COLS = 5;
const WIDE_SPAN = 3;
const NARROW_SPAN = 2;

const ITEM_GAP = 1;

const ItemRow = ({ item, onChange }: Props) => {
  const isMobile = useIsMobile();
  const unit = resolveInventoryUnit(item.unit);

  const handleChange = (consumedQuantity: number | null) =>
    onChange({ id: item.id, consumedQuantity: consumedQuantity ?? 0 });

  return (
    <Grid
      fullWidth
      align={isMobile ? "start" : "center"}
      cols={isMobile ? 1 : COLS}
      gap={isMobile ? ITEM_GAP : 4}
    >
      <Flex
        as={GridItem}
        direction="col"
        gap={ITEM_GAP}
        span={isMobile ? "full" : WIDE_SPAN}
      >
        <TruncatedText>{item.name}</TruncatedText>
        <TruncatedText variant="caption">
          {item.garden?.title ?? FALLBACK_GARDEN_TITLE}
        </TruncatedText>
      </Flex>

      <Grid
        align="center"
        as={GridItem}
        cols={COLS}
        gap={ITEM_GAP}
        span={isMobile ? "full" : NARROW_SPAN}
      >
        <Flex align="center" as={GridItem} gap={ITEM_GAP} span={WIDE_SPAN}>
          <NumberInput
            max={Number(item.quantity)}
            min={0}
            placeholder="amount"
            precision={2}
            style={{ width: 80, flexShrink: 0 }}
            onChange={handleChange}
          />
          <TruncatedText variant="caption">{unit}</TruncatedText>
        </Flex>

        <GridItem span={NARROW_SPAN}>
          <TruncatedText align="right" className="block" color="subtle" variant="caption">
            {`(${item.quantity} ${unit} left)`}
          </TruncatedText>
        </GridItem>
      </Grid>
    </Grid>
  );
};

export default ItemRow;
